import axios from 'axios'

export type NotificationType = 'ORDER_NOTIFICATION' | 'CUSTOMER_NOTIFICATION'

export interface NotificationSettings {
  id: string
  email: string
  type: NotificationType
  verified: boolean
  isPrimary: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateNotificationSettingsData {
  email: string
  type: NotificationType
}

export interface UpdateNotificationSettingsData {
  email?: string
  type?: NotificationType
}

export interface MutateNotificationSettingsResult {
  emailSetting: NotificationSettings
  verificationEmailSent: boolean
}

export const getNotificationSettings = async (): Promise<
  NotificationSettings[]
> => {
  const response = await axios.get('notification-settings', {
    withCredentials: true,
    validateStatus: () => true
  })
  if (response.status !== 200) {
    throw response.data?.error || 'Failed to fetch notification settings'
  }
  return response.data.notificationSettings
}

export const createNotificationSettings = async (
  data: CreateNotificationSettingsData
): Promise<MutateNotificationSettingsResult> => {
  const response = await axios.post('notification-settings', data, {
    withCredentials: true,
    validateStatus: () => true
  })
  if (response.status !== 201) {
    throw response.data?.error || 'Failed to create notification settings'
  }
  return {
    emailSetting: response.data.emailSetting,
    verificationEmailSent: !!response.data.verificationEmailSent
  }
}

export const updateNotificationSetting = async (
  id: string,
  data: UpdateNotificationSettingsData
): Promise<MutateNotificationSettingsResult> => {
  const response = await axios.put(`notification-settings/${id}`, data, {
    withCredentials: true,
    validateStatus: () => true
  })
  if (response.status !== 200) {
    throw response.data?.error || 'Failed to update notification settings'
  }
  return {
    emailSetting: response.data.emailSetting,
    verificationEmailSent: !!response.data.verificationEmailSent
  }
}

export const deleteNotificationSetting = async (id: string): Promise<void> => {
  const response = await axios.delete(`notification-settings/${id}`, {
    withCredentials: true,
    validateStatus: () => true
  })
  if (response.status !== 204) {
    throw response.data?.error || 'Failed to delete notification settings'
  }
}

export const resendVerification = async (
  id: string
): Promise<MutateNotificationSettingsResult> => {
  const response = await axios.post(
    `notification-settings/${id}/resend-verification`,
    {},
    { withCredentials: true, validateStatus: () => true }
  )
  if (response.status !== 200) {
    throw response.data?.error || 'Failed to resend verification email'
  }
  return {
    emailSetting: response.data.emailSetting,
    verificationEmailSent: !!response.data.verificationEmailSent
  }
}

export const refreshVerificationStatus = async (
  id: string
): Promise<NotificationSettings> => {
  const response = await axios.post(
    `notification-settings/${id}/refresh-status`,
    {},
    { withCredentials: true, validateStatus: () => true }
  )
  if (response.status !== 200) {
    throw response.data?.error || 'Failed to refresh verification status'
  }
  return response.data.emailSetting
}

export const setPrimarySender = async (
  id: string
): Promise<NotificationSettings> => {
  const response = await axios.post(
    `notification-settings/${id}/set-primary`,
    {},
    { withCredentials: true, validateStatus: () => true }
  )
  if (response.status !== 200) {
    throw response.data?.error || 'Failed to set sender'
  }
  return response.data.emailSetting
}
